"use client";

import Translations from "@translate/components/OnboardingWizard/Paywall/OnboardingFirstPaywallContent";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "next/navigation";
import { Icon } from "@ospweb/sdk-ui";
import { cn } from "utils";
import { AppImage } from "@components/NextImage";
import { useOnboardingPricingFixture } from "@hooks/onboarding/useOnboardingPricingFixture";
import { useMemberFeature } from "@hooks/useMemberFeature";
import { useMemberCompletePlans } from "@hooks/useMemberCompletePlans";
import { resolveCompletePlansFromData } from "@hooks/memberPlansData";
import { PayChannel } from "@features/api/memberApi";
import { useOnboardingPaywallCopy } from "@hooks/onboarding/useOnboardingPaywallCopy";
import { getOnboardingV3Session } from "@hooks/onboarding/onboardingV3SessionStorage";
import {
  trackObPaywallDismiss,
  trackObPaywallView,
} from "@hooks/onboarding/onboardingAnalytics";
import { OnboardingPricingCard } from "@components/Member/Subscription/OnboardingPricingCard";
import { OnboardingPricingDisclosure } from "@components/Member/Subscription/OnboardingPricingDisclosure";
import {
  buildOnboardingPricingOffers,
  isOnboardingTrialReady,
  resolveOnboardingOfferSelection,
} from "@components/Member/Subscription/onboardingPricingOffers";
import { onboardingPricingCopy } from "@components/Member/Subscription/onboardingPricingCopy";
import { pickCheckoutChannels } from "@utils/member/checkoutChannel";
import { OnboardingSecondaryChannels } from "./OnboardingSecondaryChannels";
import { I18nUtil } from "@utils/i18n";
import { useOnboardingPaywallPurchase } from "./useOnboardingPaywallPurchase";
import type { OnboardingPaywallFreeTrialContentProps } from "./types";
import styles from "./onboardingFirstPaywall.module.css";

const I18n = I18nUtil.createScoped(Translations);

export const OnboardingFirstPaywallContent = ({
  isPc,
  onClose,
  trackingModuleName,
  analyticsSource = "onboarding",
  restoredPlanId,
  checkoutPending: restoredCheckoutPending,
  onWebCheckoutRedirect,
  onPurchaseBusyChange,
}: OnboardingPaywallFreeTrialContentProps) => {
  const { locale } = useParams<{ locale?: string }>();
  const marketing = useOnboardingPaywallCopy();
  const catalog = useMemberFeature();
  const fixture = useOnboardingPricingFixture();
  const { data, trialList } = fixture ?? catalog;
  const productsLoading = !fixture && catalog.isLoading;
  const normalProducts = useMemo(
    () => data.filter((product) => product.type === "NORMAL"),
    [data],
  );
  const catalogPlans = useMemberCompletePlans({ data: normalProducts });
  const plans = fixture
    ? resolveCompletePlansFromData(normalProducts, {
        isApp: false,
        payChannel: PayChannel.STRIPE,
      })
    : catalogPlans;
  const offers = useMemo(
    () => buildOnboardingPricingOffers(plans, trialList),
    [plans, trialList],
  );
  const [selectedId, setSelectedId] = useState(restoredPlanId);
  const selected = resolveOnboardingOfferSelection(offers, selectedId);
  const {
    isLoading,
    isReady,
    handlePurchase,
    shouldTryNativePayment,
    isCheckoutPending,
  } = useOnboardingPaywallPurchase({
    firstWall: true,
    selectedProduct: selected?.purchaseProduct,
    selectedPlanId: selected?.product.id,
    onClose,
    onWebCheckoutRedirect,
    trackingModuleName,
    analyticsSource,
    checkoutPending: restoredCheckoutPending,
  });
  const checkoutPending = restoredCheckoutPending || isCheckoutPending;
  const purchaseBusy = isLoading && !checkoutPending;
  const [tickerIndex, setTickerIndex] = useState(0);
  useEffect(() => {
    setTickerIndex(0);
    if (marketing.ticker.length < 2) {
      return;
    }
    const timer = setInterval(
      () => setTickerIndex((index) => (index + 1) % marketing.ticker.length),
      3000,
    );
    return () => clearInterval(timer);
  }, [marketing.ticker]);
  const band = getOnboardingV3Session()?.band;
  const viewTracked = useRef(false);
  useEffect(() => {
    if (viewTracked.current) {
      return;
    }
    viewTracked.current = true;
    trackObPaywallView(analyticsSource, band ?? null, {
      declarativeView: true,
    });
  }, [analyticsSource, band]);
  useEffect(() => {
    onPurchaseBusyChange?.(purchaseBusy);
    return () => onPurchaseBusyChange?.(false);
  }, [purchaseBusy, onPurchaseBusyChange]);
  // 渠道顺序完全来自后端 payment_configs（EDG-552）：第一个为主 CTA，其余为「or …」次级入口
  const { primary: primaryChannel } = pickCheckoutChannels(
    selected?.purchaseProduct,
  );
  const secondaryOffers = offers.filter(
    (offer) => pickCheckoutChannels(offer.purchaseProduct).secondary.length > 0,
  );
  const purchaseReady = !!fixture || isReady;
  const primaryReady =
    !!selected &&
    !!primaryChannel &&
    isOnboardingTrialReady(selected.purchaseProduct, primaryChannel);
  const hasNoWebChannel =
    !shouldTryNativePayment && purchaseReady && !!selected && !primaryChannel;
  const copy =
    selected && purchaseReady
      ? onboardingPricingCopy(selected, locale)
      : undefined;
  const annualOffer = offers.find((offer) => offer.key === "yearly");
  const annualAnchor = annualOffer
    ? onboardingPricingCopy(annualOffer, locale).anchor
    : undefined;
  const close = () => {
    if (purchaseBusy) {
      return;
    }
    trackObPaywallDismiss(analyticsSource);
    onClose?.();
  };

  return (
    <div
      className={cn(
        "relative h-full overflow-y-auto bg-background_solid_neutral_secondary",
        styles.wall,
      )}
      data-testid="onboarding-first-paywall"
      data-harness-fixture={fixture ? "pricing-preview-no-checkout" : undefined}
      data-figma-node={isPc ? "109639:124271" : "109639:118700"}
      data-or-view="ob_paywall_view"
      data-or-param-source={analyticsSource}
      data-or-param-band={band}
      data-or-param-paywall_seq="1"
    >
      <div className="pointer-events-none absolute inset-0">
        <AppImage
          src="/assets/images/onboarding/paywall/advisor-background.webp"
          alt=""
          width={480}
          height={890}
          loading="eager"
          className="h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-content_brand_quaternary" />
        <div className={cn("absolute inset-0", styles.scrim)} />
      </div>
      <div className="relative flex min-h-full flex-col">
        <header
          className={cn(
            "flex shrink-0 px-4",
            isPc
              ? "justify-end pt-4"
              : "pt-[max(46px,var(--safe-area-inset-top,0px))]",
          )}
        >
          <button
            type="button"
            aria-label="close"
            data-openreplay-label="onboarding_first_close"
            disabled={purchaseBusy}
            onClick={close}
            className={cn(
              "flex items-center justify-center",
              isPc
                ? "size-6 text-content_inverse_primary"
                : "size-10 rounded-full bg-background_inverse_overlay100 text-content_primary",
            )}
          >
            <Icon name="close" size={24} />
          </button>
        </header>
        <div
          className={cn(
            "relative flex flex-col",
            isPc ? "gap-5 px-8 pt-8 pb-6" : "gap-6 px-6 pt-[59px] pb-6",
            styles.hero,
          )}
        >
          <div className={cn("flex items-center gap-[14px]", styles.enter)}>
            <div
              className={cn(
                "flex size-[68px] shrink-0 items-center justify-center rounded-full border-3 border-border_white_transparent10 bg-background_solid_neutral_primary p-2",
                styles.avatar,
              )}
            >
              <Icon name="ed" size={52} className="text-content_primary" />
            </div>
            <div className="min-w-0">
              <p className="title_xxl text-content_inverse_primary">
                {I18n.t("Ed")}
              </p>
              {marketing.ticker.length > 0 && (
                <div
                  className={cn(
                    "mt-[5px] flex w-fit max-w-full items-center gap-[6px] rounded-full py-[5px] pl-[10px] pr-3",
                    styles.ticker,
                  )}
                >
                  <span
                    className={cn(
                      "size-[5px] shrink-0 rounded-full bg-background_auralv3_overlay100",
                      marketing.ticker.length > 1 && styles.tickerDot,
                    )}
                  />
                  <span
                    key={`${locale}-${tickerIndex}`}
                    className={cn(
                      "overflow-x-auto whitespace-nowrap body_s text-content_inverse_secondary no-scrollbar",
                      marketing.ticker.length > 1 && styles.tickerText,
                    )}
                  >
                    {marketing.ticker[tickerIndex % marketing.ticker.length]}
                  </span>
                </div>
              )}
            </div>
          </div>
          <h1 className={cn("headline_regular_m", styles.enter)}>
            <span className="block text-content_inverse_primary">
              {marketing.titleLine1}
            </span>
            <span className="mt-[2px] block text-content_aura_lv3_primary">
              {marketing.titleLine2}
            </span>
          </h1>
          <p
            className={cn(
              "body_l text-content_inverse_secondary",
              styles.enter,
            )}
          >
            {marketing.bodyEmphasis
              ? marketing.body
                  .split(/(365|150\+?|\+?\$400|\$0)/g)
                  .map((part, index) =>
                    index % 2 === 1 ? (
                      <strong
                        key={index}
                        className="font-semibold text-content_inverse_primary"
                      >
                        {part}
                      </strong>
                    ) : (
                      part
                    ),
                  )
              : marketing.body}
          </p>
        </div>
        <div
          className={cn(
            "relative mt-auto flex flex-col",
            isPc
              ? "gap-4 bg-background_solid_neutral_primary px-6 pt-6 pb-4"
              : "gap-3 rounded-t-5 bg-background_solid_neutral_secondary pt-5 pb-[max(24px,var(--safe-area-inset-bottom,0px))]",
            styles.pricing,
          )}
        >
          <div
            className={cn(
              isPc
                ? "grid grid-cols-2 gap-3"
                : "flex gap-3 overflow-x-auto px-6 pb-1 no-scrollbar",
            )}
          >
            {offers.map((offer) => (
              <OnboardingPricingCard
                key={offer.product.id}
                offer={offer}
                selected={selected?.product.id === offer.product.id}
                locale={locale}
                disabled={isLoading || checkoutPending}
                onSelect={() => setSelectedId(offer.product.id)}
              />
            ))}
            {productsLoading && offers.length === 0 && (
              <div className="h-[142px] w-full animate-pulse rounded-[14px] bg-background_solid_neutral_tertiary" />
            )}
          </div>
          {annualAnchor && (
            <p
              className={cn(
                "body_m text-content_secondary",
                isPc ? "text-center" : "px-6",
              )}
              aria-hidden={!copy?.anchor}
              style={{ visibility: copy?.anchor ? "visible" : "hidden" }}
            >
              {annualAnchor}
            </p>
          )}
          <div className={cn("flex flex-col gap-2", !isPc && "px-6")}>
            <button
              type="button"
              data-openreplay-label="onboarding_first_purchase"
              data-or-event={
                !checkoutPending && selected
                  ? "ob_paywall_trial_click"
                  : undefined
              }
              data-or-param-source="onboarding_first"
              data-or-param-subscription_id={selected?.purchaseProduct.id}
              data-or-param-pay_cycle={selected?.product.pay_cycle}
              data-or-param-channel={primaryChannel ?? undefined}
              disabled={
                isLoading ||
                (!checkoutPending &&
                  (!purchaseReady ||
                    !selected ||
                    (!shouldTryNativePayment && !primaryReady)))
              }
              onClick={() => {
                if (!fixture) {
                  handlePurchase(primaryChannel ?? undefined);
                }
              }}
              className="button button_l emphasis_button min-h-12 w-full rounded-2 px-4 py-3"
              style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)" }}
              data-loading={isLoading}
            >
              {offers.map((offer) => (
                <span
                  key={offer.product.id}
                  aria-hidden
                  style={{ gridArea: "1 / 1", visibility: "hidden" }}
                >
                  {onboardingPricingCopy(offer, locale).cta}
                </span>
              ))}
              <span style={{ gridArea: "1 / 1" }}>
                {isLoading ? (
                  <Icon name="ic_loading2" size={20} className="animate-spin" />
                ) : checkoutPending ? (
                  // 回跳后待生效：点了是去查付款结果，不是商店的「恢复购买」
                  I18n.t("Check payment status", { locale })
                ) : hasNoWebChannel ? (
                  I18n.t("Not available", { locale })
                ) : (
                  (copy?.cta ?? I18n.t("Get Ed Complete", { locale }))
                )}
              </span>
            </button>
            {purchaseReady &&
              !shouldTryNativePayment &&
              !checkoutPending &&
              secondaryOffers.length > 0 && (
                <div className="grid" data-testid="onboarding-channel-options">
                  {secondaryOffers.map((offer) => (
                    <OnboardingSecondaryChannels
                      key={offer.product.id}
                      offer={offer}
                      locale={locale}
                      active={offer.product.id === selected?.product.id}
                      isLoading={isLoading}
                      onPurchase={(channel) => {
                        if (!fixture) {
                          handlePurchase(channel);
                        }
                      }}
                    />
                  ))}
                </div>
              )}
            {selected && purchaseReady && (
              <div className="grid">
                {offers.map((offer) => {
                  const active = offer.product.id === selected.product.id;
                  return (
                    <div
                      key={offer.product.id}
                      className="flex flex-col gap-2"
                      style={{
                        gridArea: "1 / 1",
                        visibility: active ? "visible" : "hidden",
                      }}
                      aria-hidden={!active}
                      inert={!active}
                    >
                      <OnboardingPricingDisclosure
                        offer={offer}
                        locale={locale}
                        showTermsButton
                        consentCtaLabel={
                          onboardingPricingCopy(offer, locale).cta
                        }
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
